import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAnalytics } from '@/context/Analytics';
import { IToastType } from '@/enums/ToastType';
import { useLoginMutation } from '@/hooks/useLoginMutation';
import { useToast } from '@/hooks/useToast';
import { ILoginData } from '@/resources/upgrade/Session';
import { IUser } from '@/resources/upgrade/User';

interface IFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export function useLogInForm() {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [serverErrors, setServerErrors] = useState<Array<string>>([]);
  const toast = useToast();
  const analytics = useAnalytics();

  const onLoginSuccess = async (user: IUser) => {
    analytics.identify(user.id, {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      createdAt: format(new Date(), 'yyyy-M-dd'),
    });

    analytics.track('user_login');

    toast({ status: IToastType.SUCCESS, title: 'Successfully logged in' });
  };

  const onLoginError = (error: Error) => {
    const axiosError = error as AxiosError<{ errors?: Array<{ detail: string }> }>;
    const apiErrors = axiosError.response?.data?.errors;

    if (apiErrors?.length) {
      setServerErrors(apiErrors.map((e) => e.detail));
      toast({ status: IToastType.ERROR, title: apiErrors[0]?.detail });
    } else {
      setServerErrors([error.message || 'Error']);
      toast({ status: IToastType.ERROR, title: error.message || 'Error' });
    }
  };

  const [logIn] = useLoginMutation({
    onSuccess: (session) => {
      if (session?.user) {
        onLoginSuccess(session.user);
      }
    },
    onFailure: (error) => {
      onLoginError(error);
    },
  });

  const onSubmit = async (formData: IFormData) => {
    const data: ILoginData = {
      data: {
        type: 'sessions',
        id: '',
        attributes: {
          ...formData,
        },
      },
    };

    setServerErrors([]);
    logIn(data);
  };

  return { register, watch, errors, serverErrors, onSubmit: handleSubmit(onSubmit) };
}
